import { t } from '@app/i18n';
import { useState } from 'react';
import { Alert, Button, Chip, SearchCombobox, TextInput, type ComboOption } from '@zarya/ui';
import {
  CLAIMS_SUGGESTED,
  operatorHelp,
  CLAIM_OPERATORS,
  operatorRequiresValues,
  claimsProblem,
  valuesSugeridos,
  type ClaimCondition,
  type ClaimOperator,
  operatorLabel,
} from './PolicyModel';
import s from './Policies.module.css';

export interface PolicyClaimConditionsProps {
  claims: ClaimCondition[];
  onChange: (claims: ClaimCondition[]) => void;
  editable: boolean;
}

const NEW_CLAIM: ClaimCondition = { claim: '', operator: 'in', values: [] };

/**
 * As sugestões **mais** a claim que já está na condição.
 *
 * Sem a segunda parte o combobox não acha o valor entre as opções e cai no
 * placeholder: uma política gravada com `caller_type` abriria com o campo
 * aparentemente vazio, e salvar de novo apagaria a condição em silêncio. A lista
 * é sugestão, não vocabulário (D-7c) — então o valor de fora dela é normal, e é
 * ele que precisa aparecer.
 */
function claimOptions(current: string): ComboOption[] {
  const names =
    CLAIMS_SUGGESTED.includes(current) || !current.trim()
      ? [...CLAIMS_SUGGESTED]
      : [current, ...CLAIMS_SUGGESTED];
  return names.map((claim) => {
    const values = valuesSugeridos(claim);
    // A dica é o vocabulário que o STS/PDP do lab emite naquela claim — o que
    // antes eram os rótulos dos três controles fixos da aba. Vê-lo na hora de
    // escolher a claim é o que evita descobrir `ura-voz` por tentativa.
    return {
      value: claim,
      label: claim,
      mono: true,
      ...(values.length ? { hint: values.join(' · ') } : {}),
    };
  });
}

/**
 * Editor de **condições por claim** da aba Condições (T12, D-7c / FR-085b).
 *
 * O QUE ESTA LISTA RESOLVE
 *
 *   Antes, a condição de uma política era o trio cravado `subject_type` / `amr` /
 *   `channel`, e o Rego base os comparava por nome. Uma claim nova — `caller_type`,
 *   `regiao`, o que o IdP do cliente emitir — exigia mexer no Rego, que é infra,
 *   para uma decisão que é de produto. Aqui a pessoa diz **qual** claim olhar e o
 *   Rego compara genericamente.
 *
 * OS TRÊS CONTROLES FIXOS SAÍRAM, O VOCABULÁRIO DELES FICOU (28/08)
 *
 *   A aba tinha, acima desta lista, checkboxes de **Tipo de Human Subject** e
 *   **Canal** e um combobox de **Exigir autenticação**. Aquele vocabulário
 *   (`customer/employee/prospect`, `ura-voz/sso`, `otp/mfa`) é da Vivo, não do
 *   produto — e como campo fixo ele ocupava a tela de todo cliente que não o usa.
 *
 *   Aqui ele volta como **sugestão**: o combobox da claim mostra os três nomes com
 *   os values de cada um na dica, e o campo de valor oferece esses values a um
 *   clique. Quem está no roteiro diário não digita; quem tem outro STS não é
 *   obrigado ao vocabulário de ninguém.
 */
export function PolicyClaimConditions({ claims, onChange, editable }: PolicyClaimConditionsProps) {
  const problem = claimsProblem(claims);

  function update(index: number, mudanca: Partial<ClaimCondition>) {
    onChange(claims.map((c, i) => (i === index ? { ...c, ...mudanca } : c)));
  }

  function swapOperator(index: number, operator: ClaimOperator) {
    const current = claims[index];
    // Trocar para `exists` limpa os values (o operator não os leva, e mandá-los é
    // 422); trocar para `equals` mantém só o primeiro, em vez de recusar depois — a
    // pessoa acabou de dizer "um valor só".
    let values = current.values;
    if (!operatorRequiresValues(operator)) values = [];
    else if (operator === 'equals' && values.length > 1) values = values.slice(0, 1);
    update(index, { operator, values });
  }

  return (
    <fieldset className={s.grupoDeClaims}>
      <legend className={s.legenda}>{t('humanAccess.claims.legend')}</legend>

      {/*
        O bloco vazio deixou de dizer «vale para qualquer valor» (03/09). Era
        verdade sobre o dado e mentira sobre o efeito: sem claim a regra não
        entra na interseção, e quem lia aquilo achava que tinha uma regra ampla
        quando tinha uma regra inerte. Agora ele diz o que acontece — e a mesma
        frase é o impedimento que cobra a claim ao ativar.
      */}
      {claims.length === 0 ? (
        <Alert tone="warning" title={t('humanAccess.claims.empty.title')}>
          {t('humanAccess.claims.empty.body')}
        </Alert>
      ) : null}

      <ul className={s.listaDeClaims}>
        {claims.map((condicao, index) => (
          // O índice é a identidade da linha aqui de propósito: a condição não tem
          // id próprio (o contrato a identifica pela posição), e uma chave derivada
          // do conteúdo remontaria o campo a cada tecla, perdendo o foco.
          <li key={index} className={s.linhaDeClaim}>
            <div className={s.claimCampos}>
              <SearchCombobox
                label={t('humanAccess.claims.claimOf', { n: index + 1 })}
                placeholder={t('humanAccess.claims.claim.placeholder')}
                searchPlaceholder={t('humanAccess.claims.claim.searchPlaceholder')}
                emptyMessage={t('humanAccess.claims.claim.empty')}
                disabled={!editable}
                creatable
                onCreate={(texto) => update(index, { claim: texto.trim() })}
                options={claimOptions(condicao.claim)}
                value={condicao.claim || null}
                onChange={(valor) => update(index, { claim: valor ?? '' })}
                invalid={problem?.indice === index && !condicao.claim.trim()}
              />

              <SearchCombobox
                label={t('humanAccess.claims.operatorOf', { n: index + 1 })}
                placeholder={t('humanAccess.claims.operator.placeholder')}
                disabled={!editable}
                options={CLAIM_OPERATORS.map((operator) => ({
                  value: operator,
                  label: operatorLabel(operator),
                  hint: operatorHelp(operator),
                }))}
                value={condicao.operator}
                onChange={(valor) => swapOperator(index, (valor as ClaimOperator) ?? 'in')}
              />

              {editable ? (
                <Button
                  variant="soft"
                  size="sm"
                  icon="trash"
                  onClick={() => onChange(claims.filter((_, i) => i !== index))}
                  aria-label={t('humanAccess.claims.remove.aria', { n: index + 1 })}
                >
                  {t('humanAccess.claims.remove')}
                </Button>
              ) : null}
            </div>

            {operatorRequiresValues(condicao.operator) ? (
              <ClaimValues
                indice={index}
                claim={condicao.claim}
                values={condicao.values}
                soUmValor={condicao.operator === 'equals'}
                editable={editable}
                onChange={(values) => update(index, { values })}
              />
            ) : (
              <span className={s.ajuda}>{t('field.existsTakesNoValues')}</span>
            )}

            {problem?.indice === index ? (
              <span className={s.erroInline} role="alert">
                {problem.message}
              </span>
            ) : null}
          </li>
        ))}
      </ul>

      {editable ? (
        <Button
          variant="out"
          size="sm"
          icon="plus"
          onClick={() => onChange([...claims, { ...NEW_CLAIM }])}
        >
          {t('humanAccess.claims.add')}
        </Button>
      ) : null}
    </fieldset>
  );
}

interface ClaimPropsValues {
  indice: number;
  claim: string;
  values: string[];
  soUmValor: boolean;
  editable: boolean;
  onChange: (values: string[]) => void;
}

/**
 * Os values aceitos, como chips. Enter (ou o botão) acrescenta; o «×» do chip
 * remove.
 *
 * O campo **não** se limpa sozinho quando `equals` já tem um valor: o botão fica
 * desabilitado com o motivo à vista, porque um Enter que não faz nada e não diz
 * por quê é a pior das duas.
 *
 * Quando a claim tem vocabulário conhecido (`subject_type`, `channel`, `amr` —
 * o que os três controles fixos ofereciam até 28/08), os values que faltam
 * aparecem como botões: é o mesmo clique de antes, sem cravar os campos na tela.
 * O campo de texto continua ali — a sugestão nunca fecha o conjunto.
 */
function ClaimValues({ indice, claim, values, soUmValor, editable, onChange }: ClaimPropsValues) {
  const [rascunho, setRascunho] = useState('');
  const full = soUmValor && values.length >= 1;
  const sugestoes = valuesSugeridos(claim).filter((valor) => !values.includes(valor));

  function add(valor: string) {
    const clean = valor.trim();
    if (!clean || full || values.includes(clean)) return;
    onChange([...values, clean]);
  }

  return (
    <div className={s.claimValores}>
      <div className={s.chips}>
        {values.map((valor) => (
          <Chip
            key={valor}
            onRemove={editable ? () => onChange(values.filter((v) => v !== valor)) : undefined}
            removeLabel={t('humanAccess.claims.values.removeValue', { value: valor })}
          >
            {valor}
          </Chip>
        ))}
        {values.length === 0 ? (
          <span className={s.ajuda}>{t('humanAccess.claims.values.none')}</span>
        ) : null}
      </div>

      {editable ? (
        <>
          <div className={s.claimCampos}>
            <TextInput
              aria-label={t('humanAccess.claims.values.newAria', { n: indice + 1 })}
              placeholder={t(
                full
                  ? 'humanAccess.claims.values.placeholderFull'
                  : 'humanAccess.claims.values.placeholder',
              )}
              value={rascunho}
              disabled={full}
              onChange={(e) => setRascunho(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                // Sem isto o Enter submete o formulário do drawer e salva a
                // política no meio da edição da condição.
                e.preventDefault();
                add(rascunho);
                setRascunho('');
              }}
            />
            <Button
              variant="out"
              size="sm"
              onClick={() => {
                add(rascunho);
                setRascunho('');
              }}
              disabled={full || !rascunho.trim()}
            >
              {t('humanAccess.claims.values.append')}
            </Button>
          </div>

          {sugestoes.length && !full ? (
            <div className={s.sugestoes}>
              <span className={s.sugestoesRotulo}>
                {t('humanAccess.claims.values.suggestions')}
              </span>
              {sugestoes.map((valor) => (
                <Button
                  key={valor}
                  variant="soft"
                  size="sm"
                  aria-label={t('humanAccess.claims.values.appendAria', {
                    value: valor,
                    n: indice + 1,
                  })}
                  onClick={() => add(valor)}
                >
                  {valor}
                </Button>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
